import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { API, apiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await API.get("/settings");
      setSettings(data);
    } catch {
      setSettings(null);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
      fetchSettings();
    } catch {
      setUser(false);
    }
  }, [fetchSettings]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await API.post("/auth/login", { email, password });
      if (data?.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      fetchSettings();
      // Welcome banner — show branch name if available, otherwise the user's name.
      let branchName = "";
      if (data.user?.branch_id) {
        try {
          const { data: branches } = await API.get("/branches");
          branchName = branches.find((b) => b.id === data.user.branch_id)?.name || "";
        } catch { /* ignore */ }
      }
      toast.success(
        branchName
          ? `Welcome, ${data.user.name} — ${branchName}`
          : `Welcome, ${data.user.name}`,
        { duration: 4000 }
      );
      return true;
    } catch (e) {
      setError(apiError(e));
      return false;
    }
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
    setSettings(null);
  };

  return (
    <AuthContext.Provider value={{ user, settings, error, login, logout, refresh: fetchMe, refreshSettings: fetchSettings }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
